import React, { forwardRef, useEffect, useRef, useState, Suspense } from "react";
import * as THREE from "three";
import { MOUSE } from "three";
import { Canvas } from "@react-three/fiber";
import { Spaceship } from "./Spaceship";
import { Robot } from "./Robot";
import OtherPlayerSpaceship from "./OtherPlayerSpaceship";
import OtherPlayerRobot from "./OtherPlayerRobot";
import MultiplayerManager, { PlayerState, MultiplayerManagerRef } from "./MultiplayerManager";
import { OrbitControls, Html, useGLTF } from "@react-three/drei";
import { useLanguage } from '../i18n/LanguageContext';
import { collisionSystem } from '../utils/CollisionSystem';
import { mmlCollisionSystem } from '../utils/MMLCollisionSystem';
import { animatedParcelTracker } from '../utils/AnimatedParcelTracker';
import StarryBackground from './StarryBackground';
// Import de controles de áudio removido
import Moon3D from './Moon3D';
import Earth3D from './Earth3D';
import SpeakingEffect from './SpeakingEffect';
import { MMLCube, MMLParser } from '../utils/MMLParser';
import MMLCubeRenderer from './MMLCubeRenderer';
import FogDisabler from './FogDisabler';
import FixedSpaceshipWithSpotlight from './FixedSpaceshipWithSpotlight';
import FixedArrow from './FixedArrow';
import defaultCastleConfig from '../utils/CastleCollisionConfig';
import { CollisionBoxVisualizer } from './CollisionBoxVisualizer';
import CollisionBoxesVisualizer from './CollisionBoxesVisualizer';

// Componente simplificado para o Fantasy Castle
const SimpleCastle = ({ position = [0, 0, 0], scale = [5, 5, 5] }) => {
    const { scene } = useGLTF('/models/gltf/fantasy_castle/scene.gltf');
    const modelRef = useRef<THREE.Group>();

    useEffect(() => {
        if (scene && modelRef.current) {
            try {
                // Criar uma cópia do modelo para não afetar o original
                const model = scene.clone();

                // Configurar sombras para todos os objetos do modelo
                model.traverse((child) => {
                    if (child instanceof THREE.Mesh) {
                        child.castShadow = true;
                        child.receiveShadow = true;

                        // Melhorar a qualidade dos materiais
                        if (child.material) {
                            // Se for um array de materiais
                            if (Array.isArray(child.material)) {
                                child.material.forEach(material => {
                                    if (material.map) material.map.anisotropy = 16;
                                    material.side = THREE.DoubleSide;
                                    material.needsUpdate = true;
                                });
                            }
                            // Se for um único material
                            else {
                                if (child.material.map) child.material.map.anisotropy = 16;
                                child.material.side = THREE.DoubleSide;
                                child.material.needsUpdate = true;
                            }
                        }
                    }
                });

                // Aplicar escala
                model.scale.set(scale[0], scale[1], scale[2]);

                // Limpar filhos existentes
                while (modelRef.current.children.length > 0) {
                    modelRef.current.remove(modelRef.current.children[0]);
                }

                // Adicionar o novo modelo
                modelRef.current.add(model);

                // Usar a configuração do arquivo CastleCollisionConfig.ts
                // Isso garante que as alterações feitas no arquivo sejam aplicadas
                if (process.env.NODE_ENV === 'development') {
                    console.log('Usando configuração do castelo do arquivo CastleCollisionConfig.ts:', defaultCastleConfig);
                }

                // Não precisamos calcular a caixa de colisão, pois já temos a configuração
                // Apenas adicionamos a barreira ao sistema de colisão
                mmlCollisionSystem.setBarrier(defaultCastleConfig);

            } catch (error) {
                console.error('Erro ao configurar o modelo Fantasy Castle:', error);
            }
        }
    }, [scene, position, scale]);

    return <group ref={modelRef as any} position={[position[0], position[1], position[2]]} />;
};

// Pré-carregar o modelo para melhor desempenho
useGLTF.preload('/models/gltf/fantasy_castle/scene.gltf');

interface MMLRendererMultiplayerProps {
    mmlContent: string;
    style?: React.CSSProperties;
    onParcelHover?: (id: string | null) => void;
    onParcelSelect?: (id: string) => void;
    selectedParcel?: string | null;
    onCurrentParcel?: (id: string | null) => void;
    showCollisionBoxes?: boolean;
    hasMultiversoPass?: boolean; // Indica se o usuário possui um Multiverso Pass
}

// Scene component that will contain our visualization
const Scene = ({
    mmlContent,
    onParcelHover,
    onParcelSelect,
    selectedParcel,
    onCurrentParcel,
    multiplayerRef,
    otherPlayers,
    speakingPlayers = new Map(),
    showCollisionBoxes = false,
    hasMultiversoPass = false
}: any) => {
    const { t } = useLanguage();
    const [cubes, setCubes] = useState<MMLCube[]>([]);
    const [hovered, setHovered] = useState<string | null>(null);
    const [hoveredPosition, setHoveredPosition] = useState<THREE.Vector3 | null>(null);
    // Ajustar a posição inicial da nave para as coordenadas exatas fornecidas
    // Posição exata do jogador: x: -16.709381179446595, y: 4.800000000000001, z: 253.78507062164317
    const [spaceshipPosition, setSpaceshipPosition] = useState<[number, number, number]>([-16.709381179446595, 4.800000000000001, 253.78507062164317]);

    // Manter a nave reta, sem inclinação
    // Isso garante que a nave esteja nivelada e não inclinada
    const [spaceshipRotation, setSpaceshipRotation] = useState<[number, number, number]>([
        0, // Sem inclinação vertical (nave reta)
        0, // Sem rotação horizontal (nave reta)
        0  // Sem rotação lateral (nave reta)
    ]);

    // Parse MML content
    useEffect(() => {
        if (mmlContent) {
            try {
                // Aplicar o fator de escala (2x) ao analisar o MML
                const parsedCubes = MMLParser.parse(mmlContent, true);
                setCubes(parsedCubes);

                // Configure collision systems
                const worldDimensions = MMLParser.getWorldDimensions(parsedCubes);

                // Update old collision system (for compatibility)
                collisionSystem.clearBarriers();
                collisionSystem.worldWidth = worldDimensions.width;
                collisionSystem.worldHeight = worldDimensions.height;

                // Update MML collision system
                mmlCollisionSystem.setBarriersFromMML(parsedCubes);

                // Convert MML cubes to barriers for old collision system (for compatibility)
                const mmlBarriers = MMLParser.convertToBarriers(parsedCubes);
                collisionSystem.setBarriersFromMondrian(mmlBarriers);

                console.log('MML Parcels configured:', parsedCubes.length);
            } catch (error) {
                console.error('Error parsing MML:', error);
            }
        }
    }, [mmlContent]);

    // Handle spaceship position updates
    const handleSpaceshipPosition = (position: THREE.Vector3, rotation: THREE.Euler) => {
        setSpaceshipPosition([position.x, position.y, position.z]);
        setSpaceshipRotation([rotation.x, rotation.y, rotation.z]);

        // Find which parcel we're over
        if (cubes.length > 0) {
            // Convert world coordinates to MML coordinates
            const mmlX = position.x;
            const mmlZ = position.z;

            // Find the parcel we're over
            let foundParcel: string | null = null;
            for (const cube of cubes) {
                if (
                    mmlX >= cube.x &&
                    mmlX <= cube.x + cube.width &&
                    mmlZ >= cube.z &&
                    mmlZ <= cube.z + cube.depth
                ) {
                    foundParcel = cube.id;
                    break;
                }
            }

            // Atualizar a parcela atual no rastreador de parcelas animadas
            animatedParcelTracker.setCurrentParcel(foundParcel);
            onCurrentParcel?.(foundParcel);
        }

        // Update multiplayer position
        if (multiplayerRef.current) {
            multiplayerRef.current.updatePosition(
                {
                    x: position.x,
                    y: position.y,
                    z: position.z
                },
                {
                    x: rotation.x,
                    y: rotation.y,
                    z: rotation.z
                }
            );
        }
    };

    // Handle cube hover
    const handleCubeHover = (cube: MMLCube) => {
        setHovered(cube.id);
        setHoveredPosition(new THREE.Vector3(
            cube.x + cube.width / 2,
            cube.height + 0.5,
            cube.z + cube.depth / 2
        ));
        onParcelHover?.(cube.id);
    };

    // Handle cube unhover
    const handleCubeUnhover = () => {
        setHovered(null);
        setHoveredPosition(null);
        onParcelHover?.(null);
    };

    // Handle cube click
    const handleCubeClick = (cube: MMLCube) => {
        // Não fazer nada ao clicar no parcel - não seleciona e não reposiciona a nave
        // onParcelSelect?.(cube.id); // Comentado para evitar a seleção do parcel
    };

    // Posicionar a nave acima da parcela selecionada apenas quando ela for pesquisada
    useEffect(() => {
        // Verificar se há um parcel selecionado, se há cubes e se o parcel foi selecionado via pesquisa
        if (selectedParcel && cubes.length > 0 && selectedParcel.startsWith('parcel-')) {
            // Procurar por parcela com ID que comece com o prefixo da pesquisa
            const selectedCube = cubes.find(cube => cube.id.startsWith(selectedParcel));

            if (selectedCube) {
                console.log('Found parcel from search:', selectedCube.id);
                const newPosition = new THREE.Vector3(
                    selectedCube.x + selectedCube.width / 2,
                    selectedCube.height + 10, // 10 unidades acima da parcela
                    selectedCube.z + selectedCube.depth / 2
                );
                setSpaceshipPosition([newPosition.x, newPosition.y, newPosition.z]);

                // Atualizar a posição no multiplayer
                if (multiplayerRef.current) {
                    multiplayerRef.current.updatePosition(
                        {
                            x: newPosition.x,
                            y: newPosition.y,
                            z: newPosition.z
                        },
                        {
                            x: spaceshipRotation[0],
                            y: spaceshipRotation[1],
                            z: spaceshipRotation[2]
                        }
                    );
                }
            }
        }
    }, [selectedParcel, cubes]);

    return (
        <>
            {/* Desativar o fog para melhorar a visibilidade das parcelas distantes */}
            <FogDisabler />
            {/* Luz direcional principal - simula o sol */}
            <directionalLight
                position={[100, 100, 100]}
                intensity={0.9}
                castShadow
                shadow-mapSize={[2048, 2048]}
                shadow-bias={-0.001}
            />

            {/* Luz direcional secundária - preenchimento do lado oposto */}
            <directionalLight
                position={[-50, 80, -50]}
                intensity={0.4}
                castShadow
                shadow-mapSize={[1024, 1024]}
                shadow-bias={-0.001}
            />

            {/* Luz ambiente para iluminação geral */}
            <ambientLight intensity={0.5} />

            {/* Luz de preenchimento para destacar detalhes */}
            <pointLight position={[0, 100, 0]} intensity={0.3} distance={500} decay={2} />

            {/* Visualizadores de caixas de colisão removidos para o frontend */}

            {/* Render MML cubes */}
            {cubes.map((cube) => (
                <MMLCubeRenderer
                    key={cube.id}
                    cube={cube}
                    onClick={() => handleCubeClick(cube)}
                    onHover={() => handleCubeHover(cube)}
                    onUnhover={handleCubeUnhover}
                    isSelected={selectedParcel === cube.id}
                />
            ))}

            {/* Player do jogador local - Spaceship para quem tem Multiverso Pass, Robot para os demais */}
            {hasMultiversoPass ? (
                <Spaceship
                    position={spaceshipPosition}
                    rotation={spaceshipRotation}
                    onPositionChange={handleSpaceshipPosition}
                    otherShips={otherPlayers.map((player: any) => ({
                        position: new THREE.Vector3(player.position.x, player.position.y, player.position.z)
                    }))}
                    playerId={multiplayerRef.current?.getLocalSocketId() || 'local'}
                />
            ) : (
                <Robot
                    position={spaceshipPosition}
                    rotation={spaceshipRotation}
                    onPositionChange={handleSpaceshipPosition}
                    otherPlayers={otherPlayers.map((player: any) => ({
                        position: new THREE.Vector3(player.position.x, player.position.y, player.position.z)
                    }))}
                    playerId={multiplayerRef.current?.getLocalSocketId() || 'local'}
                />
            )}

            {/* Players de outros jogadores - Spaceship para quem tem Multiverso Pass, Robot para os demais */}
            {otherPlayers.map((player: any) => (
                <React.Fragment key={player.socketId}>
                    {player.hasMultiversoPass ? (
                        <OtherPlayerSpaceship
                            playerId={player.socketId}
                            position={[player.position.x, player.position.y, player.position.z]}
                            rotation={[player.rotation.x, player.rotation.y, player.rotation.z]}
                            velocity={player.velocity}
                            timestamp={player.timestamp}
                        />
                    ) : (
                        <OtherPlayerRobot
                            playerId={player.socketId}
                            position={[player.position.x, player.position.y, player.position.z]}
                            rotation={[player.rotation.x, player.rotation.y, player.rotation.z]}
                            velocity={player.velocity}
                            timestamp={player.timestamp}
                        />
                    )}

                    {/* Efeito de ondas sonoras para o player remoto quando estiver falando */}
                    <SpeakingEffect
                        position={new THREE.Vector3(player.position.x, player.position.y, player.position.z)}
                        active={speakingPlayers.get(player.socketId) || false}
                        color="#2196F3"
                    />
                </React.Fragment>
            ))}

            {/* Fundo estrelado - aumentado significativamente para criar um universo mais amplo */}
            <StarryBackground count={25000} depth={2000} size={0.7} color="#ffffff" twinkleSpeed={0.01} />

            {/* Lua 3D do Bitcoin Ordinal (inscription #76545890) - Posição e escala ajustadas manualmente */}
            <Moon3D position={[-500, 100, -700]} scale={100} rotationSpeed={0.0005} />

            {/* Planeta Terra 3D visível no horizonte */}
            <Earth3D position={[500, 60, -400]} scale={60} />

            {/* Spaceship fixo com luz de destaque acima do parcel 89 */}
            <FixedSpaceshipWithSpotlight
                position={[23, 20, 16]}
                mintUrl="https://inscribenow.io/collections/38ad28c5d73e92ec"
                marketplaceUrl="https://magiceden.io/ordinals/marketplace/multiverso"
            />

            {/* Seta indicativa fixa acima do spaceship fixo - componente separado */}
            <FixedArrow position={[23, 32, 16]} />

            {/* Modelo 3D Fantasy Castle acima do parcel 70 - sem rotação */}
            <group
                position={[-18, 10, 319]}
                rotation={[0, Math.PI / 2, 0]}
            >
                <Suspense fallback={null}>
                    <SimpleCastle position={[0, 0, 0]} scale={[1.45, 1.45, 1.45]} />
                </Suspense>
            </group>

            {/* Luz adicional para destacar o modelo */}
            <spotLight
                position={[100, 60, 200]}
                angle={0.7}
                penumbra={0.5}
                intensity={3}
                color="#FFFFFF"
                castShadow
                target-position={[100, 30, 200]}
            />

            {/* Luz ambiente adicional para o modelo */}
            <pointLight
                position={[100, 30, 200]}
                intensity={2}
                distance={50}
                color="#FFD700"
            />

            {/* Label para mostrar informação do bloco */}
            {hovered !== null && hoveredPosition && (
                <Html position={hoveredPosition}>
                    <div style={{
                        background: 'rgba(0,0,0,0.8)',
                        color: 'white',
                        padding: '8px',
                        borderRadius: '4px',
                        fontSize: '14px',
                        transform: 'translate(-50%, -100%)',
                        whiteSpace: 'nowrap'
                    }}>
                        {t('parcel')} {hovered.replace('parcel-', '').split('-')[0]}
                    </div>
                </Html>
            )}

            {/* Ground plane */}
            <mesh
                rotation={[-Math.PI / 2, 0, 0]}
                position={[0, -0.1, 0]}
                receiveShadow
            >
                <planeGeometry args={[2000, 2000]} />
                <shadowMaterial transparent opacity={0.2} />
            </mesh>
        </>
    );
};

const MMLRendererMultiplayer = forwardRef<MultiplayerManagerRef, MMLRendererMultiplayerProps>(({
    // Props
    mmlContent,
    style,
    onParcelHover,
    onParcelSelect,
    selectedParcel,
    onCurrentParcel,
    showCollisionBoxes = false,
    hasMultiversoPass = false
}, ref) => {
    const [speakingPlayers, setSpeakingPlayers] = useState<Map<string, boolean>>(new Map());
    const [otherPlayers, setOtherPlayers] = useState<PlayerState[]>([]);
    // Estado para rastrear a mudança no status do Multiverso Pass
    const prevHasMultiversoPassRef = useRef<boolean>(hasMultiversoPass);
    const multiplayerRef = useRef<MultiplayerManagerRef>(null);

    // Forward ref - garantir que a referência seja passada corretamente
    React.useImperativeHandle(ref, () => ({
        // Métodos expostos
        updatePosition: (position: any, rotation: any) => {
            if (multiplayerRef.current) {
                multiplayerRef.current.updatePosition(position, rotation);
            }
        },
        updateMultiversoPassStatus: (hasAccess: boolean) => {
            if (multiplayerRef.current) {
                console.log(`[MMLRendererMultiplayer] Encaminhando updateMultiversoPassStatus(${hasAccess}) para MultiplayerManager`);
                multiplayerRef.current.updateMultiversoPassStatus(hasAccess);

                // Forçar uma única atualização da posição para garantir que o modelo correto seja exibido
                const currentPosition = multiplayerRef.current.getLocalPosition();
                const currentRotation = multiplayerRef.current.getLocalRotation();

                // Pequeno atraso para garantir que o status seja atualizado primeiro
                setTimeout(() => {
                    multiplayerRef.current?.updatePosition(currentPosition, currentRotation);
                }, 500);
            }
        },
        getLocalSocketId: () => {
            return multiplayerRef.current ? multiplayerRef.current.getLocalSocketId() : null;
        },
        getAllPlayers: () => {
            return multiplayerRef.current ? multiplayerRef.current.getAllPlayers() : [];
        },
        getLocalPosition: () => {
            return multiplayerRef.current ? multiplayerRef.current.getLocalPosition() : { x: 0, y: 0, z: 0 };
        },
        getLocalRotation: () => {
            return multiplayerRef.current ? multiplayerRef.current.getLocalRotation() : { x: 0, y: 0, z: 0 };
        },
        getSocket: () => {
            return multiplayerRef.current ? multiplayerRef.current.getSocket() : null;
        }
    }));

    // Handle players update
    const handlePlayersUpdate = (players: PlayerState[]) => {
        // Reduzir logs repetitivos - apenas logar em desenvolvimento ou quando houver mudança
        const shouldLog = process.env.NODE_ENV === 'development' ||
                         (previousPlayersCountRef.current !== players.length);

        if (shouldLog) {
            console.log('Received players update:', players.length, 'players');
        }

        // Atualizar a referência para a próxima comparação
        previousPlayersCountRef.current = players.length;

        if (players.length > 0) {
            const localSocketId = multiplayerRef.current?.getLocalSocketId();

            // Filtrar jogadores remotos (excluindo o jogador local)
            let remotePlayers = players.filter(p => p.socketId !== localSocketId);

            // Filtrar jogadores duplicados por ID persistente
            const uniquePlayers: PlayerState[] = [];
            const seenPersistentIds = new Map<string, PlayerState>();

            // Primeiro, agrupar por ID persistente e manter apenas o mais recente
            remotePlayers.forEach((player: PlayerState) => {
                const persistentId = player.persistentId || player.socketId;

                if (seenPersistentIds.has(persistentId)) {
                    // Se já temos um jogador com este ID persistente, manter o mais recente
                    const existingPlayer = seenPersistentIds.get(persistentId)!;
                    const existingTimestamp = existingPlayer.timestamp || 0;
                    const newTimestamp = player.timestamp || 0;

                    if (newTimestamp > existingTimestamp) {
                        seenPersistentIds.set(persistentId, player);
                    }
                } else {
                    // Primeira vez que vemos este ID persistente
                    seenPersistentIds.set(persistentId, player);
                }
            });

            // Converter o mapa de volta para um array
            remotePlayers = Array.from(seenPersistentIds.values());

            // Reduzir logs repetitivos - apenas logar em desenvolvimento ou quando houver mudança
            if (shouldLog) {
                console.log('Remote players (after deduplication):', remotePlayers.length);
            }

            setOtherPlayers(remotePlayers);
        } else if (otherPlayers.length > 0) {
            // Se não há jogadores, limpar a lista
            setOtherPlayers([]);
        }
    };

    // Referência para armazenar a contagem anterior de jogadores
    const previousPlayersCountRef = useRef<number>(0);

    // Handle speaking status update
    const handleSpeakingUpdate = (playerId: string, isSpeaking: boolean) => {
        setSpeakingPlayers(prev => {
            const newMap = new Map(prev);
            newMap.set(playerId, isSpeaking);
            return newMap;
        });
    };

    // Não precisamos verificar periodicamente os jogadores remotos
    // O MultiplayerManager já notifica sobre mudanças através do callback onPlayersUpdate

    // Prevenir o zoom do navegador quando o usuário usa a roda do mouse com Ctrl pressionado
    useEffect(() => {
        const preventZoom = (e: WheelEvent) => {
            if (e.ctrlKey) {
                e.preventDefault();
            }
        };

        // Adicionar o listener com a opção passive: false para permitir preventDefault
        document.addEventListener('wheel', preventZoom, { passive: false });

        // Remover o listener quando o componente for desmontado
        return () => {
            document.removeEventListener('wheel', preventZoom);
        };
    }, []);

    // Efeito para detectar mudanças no status do Multiverso Pass
    useEffect(() => {
        // Se o status mudou, registramos no console
        if (prevHasMultiversoPassRef.current !== hasMultiversoPass) {
            console.log(`[MMLRendererMultiplayer] Status do Multiverso Pass alterado: ${prevHasMultiversoPassRef.current} -> ${hasMultiversoPass}`);

            // Atualizar a referência para o novo valor
            prevHasMultiversoPassRef.current = hasMultiversoPass;

            // Atualizar o status no MultiplayerManager
            if (multiplayerRef.current) {
                // Atualizar o status do Multiverso Pass - isso já inclui a atualização de posição
                multiplayerRef.current.updateMultiversoPassStatus(hasMultiversoPass);
            }
        }
    }, [hasMultiversoPass]);

    // Ocultar todas as caixas de colisão no frontend
    const isShowingCollisionBoxes = false;

    return (
        <div
            style={{ ...style, position: 'relative' }}
            onWheel={(e) => {
                // Prevenir o comportamento padrão de zoom do navegador
                // Prevenir sempre, não apenas quando Ctrl está pressionado
                e.preventDefault();
            }}
        >
            {/* Botão de caixas de colisão removido */}
            <Canvas
                shadows
                camera={{
                    // Posição da câmera para mostrar mais do horizonte e permitir maior zoom in
                    // Posição mais elevada para ver além das parcelas e mostrar o céu
                    position: [-14.5, 100, 355], // Posição mais elevada para ver mais do horizonte
                    fov: 60, // Campo de visão ajustado para melhor visualização
                    near: 0.1, // Plano próximo menor para ver objetos próximos
                    far: 3000 // Plano distante aumentado para ver objetos mais distantes
                }}
                gl={{
                    antialias: true,
                    toneMapping: THREE.ACESFilmicToneMapping,
                    toneMappingExposure: 0.8,
                    powerPreference: 'high-performance',
                    failIfMajorPerformanceCaveat: false
                }}

                onCreated={({ gl }) => {
                    // Adicionar tratamento para perda de contexto
                    const canvas = gl.domElement;
                    canvas.addEventListener('webglcontextlost', (event: Event) => {
                        event.preventDefault();
                        console.warn('WebGL context lost. Attempting to restore...');
                        // Forçar um recarregamento da página após 2 segundos
                        setTimeout(() => {
                            window.location.reload();
                        }, 2000);
                    });
                }}
            >
                <Scene
                    mmlContent={mmlContent}
                    onParcelHover={onParcelHover}
                    onParcelSelect={onParcelSelect}
                    selectedParcel={selectedParcel}
                    onCurrentParcel={onCurrentParcel}
                    multiplayerRef={multiplayerRef}
                    otherPlayers={otherPlayers}
                    speakingPlayers={speakingPlayers}
                    showCollisionBoxes={isShowingCollisionBoxes} // Passar o estado atual das caixas de colisão
                    hasMultiversoPass={hasMultiversoPass} // Passar o status do Multiverso Pass
                />
                <OrbitControls
                    makeDefault
                    enableZoom={false} // Desativado para usar nossa implementação personalizada de zoom
                    minDistance={1}
                    maxDistance={1000}
                    enableDamping={true}
                    dampingFactor={0.2}
                    screenSpacePanning={true} // Melhora o comportamento do pan
                    // Configurar a posição inicial do alvo para olhar mais para cima
                    // Isso faz a câmera olhar mais para o céu, como se o mouse estivesse apontado para cima
                    target={[-14.5, 70, 250]} // Olhando para um ponto ainda mais elevado no horizonte
                    mouseButtons={{
                        LEFT: MOUSE.ROTATE,
                        MIDDLE: MOUSE.DOLLY,
                        RIGHT: MOUSE.PAN
                    }}
                />
                <MultiplayerManager
                    ref={multiplayerRef}
                    onLocalPlayerMove={(position, _rotation) => {
                        // Apenas para debug em ambiente de desenvolvimento
                        if (process.env.NODE_ENV === 'development') {
                            console.log('Received position update from server:', position);
                        }
                    }}
                    onPlayersUpdate={handlePlayersUpdate}
                />
            </Canvas>

            {/* Controles de áudio removidos - agora usando Daily.co */}


        </div>
    );
});

export default MMLRendererMultiplayer;
